import Link from "next/link";
import { Stack } from "@mui/material";
import { MdInfoOutline } from "react-icons/md";

import { routes } from "src/shared/routes";

import { SSText } from "src/components/base/components/SSText";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Register Your Pass",
    description:
      "Register your Cyclone Mobile Pass and link your accounts with other Pass purchasers for a chance to have your seats placed together, subject to availability. You must use the same email address you purchased your Pass with to register.",
    authRedirectPage: routes.my_group,
    buttonLabel: "Register",
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "You are guaranteed tickets to all games on the schedule. Tickets are automatically sent for all games the day before.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Tickets are guaranteed for the games defined in the schedule. There are no penalties for not attending a game.",
  },
  {
    question: "How do I access my tickets?",
    answer:
      "You will receive a text message at 9AM the day before the game with a link to access your tickets.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat locations will vary by game.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Cyclones Mobile Pass tickets, as well as guest passes, are not eligible for resale or transfer. Abusing these guidelines may result in your Cyclones Mobile Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <Link href="mailto:tickets@iastate.edu">tickets@iastate.edu</Link> for
        any assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        Yes, you can create a group to link with other pass holders! Navigate to
        the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "iowastate_cyclonesbasketball" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        together for the game. You will need to ensure your group is created and
        all members have joined at 48 hours prior to game start in order to be
        seated with one another.
      </>
    ),
  },
  {
    question: "Can I bring a friend to a game",
    answer:
      "Guest tickets will be available for select games. If available, you will be notified about guest tickets available for purchase via email.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer:
      "No, we do not issue any refunds for games you are not able to attend. Passes are non-transferable and non-refundable.",
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    By checking this box you agree to receive notifications from Iowa State
    University and the{" "}
    <Link target="_blank" href="https://www.attend.tech/terms-of-use">
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link target="_blank" href="https://www.attend.tech/privacy-policy">
      Privacy Policy
    </Link>
    .
  </>
);

export const ADD_ON_PURCHASE_SUCCESS_MESSAGE = {
  TITLE: () => <>You&apos;re all set to experience the action!</>,
  DESCRIPTION: () => (
    <>
      Invite your friends! Create groups{" "}
      <b>
        <u>
          <Link
            href={{
              pathname: routes.my_group,
              query: { teamSchema: "iowastate_cyclonesbasketball" },
            }}
          >
            here
          </Link>
        </u>
      </b>
      .
    </>
  ),
};

export const SWAF_INFO_TEXT = () => (
  <Stack
    p={1}
    my={2}
    gap={1}
    borderRadius={2}
    bgcolor="#CEE8F0"
    alignItems="center"
    flexDirection="row"
    flexWrap="nowrap"
  >
    <MdInfoOutline
      color="#014361"
      style={{
        flexShrink: 0,
        fontSize: 22,
        minWidth: 24,
        minHeight: 24,
      }}
    />
    <SSText
      variant="body2"
      color="#014361"
      sx={{
        wordBreak: "break-word",
        fontSize: { xs: "1rem", sm: "1rem" },
        lineHeight: 1.4,
        fontWeight: 400,
        display: "inline",
      }}
    >
      Group formation closes Tuesday at 5PM the week prior to a game. Any groups
      formed after that time will not be accounted for in your seat assignment.
    </SSText>
  </Stack>
);
